import React from 'react';
import { Avatar, Chip, Tooltip } from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import {
  ChartSquare,
  GroupUserIc,
  MessageIc,
  MoneyIc,
  RefreshGreyIc,
  TicketBillIc,
  UserCheckIc,
  UserNoAvatarIcon,
  WalletIc,
} from '../../../../components/icons';
import { PartnerOrderData } from '../types';
import { getPlatformIconForRender } from '../../../../utils';
import { getStatusLabel } from '../utils';
import { useTranslations } from '../../../../contexts/translation.context';
import { Progress } from '@heroui/react';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';

interface OfferCellProps {
  order: PartnerOrderData;
}

const OfferCell: React.FC<OfferCellProps> = ({ order }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'bg-blue-600';
      case 'waiting':
        return 'bg-yellow-600';
      case 'completed':
        return 'bg-green-600';
      case 'canceled':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };
  const { t } = useTranslations();

  // Функция для замены переменных в строке перевода
  const interpolate = (template: string, vars: Record<string, any>): string => {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? '');
  };

  // Расчеты для тултипов
  const grossTotal = order.statistics.totalPrice || 0;
  const netRevenueTotal = order.statistics.netRevenue ?? order.statistics.transactions.total ?? 0;
  const pricePerFan = order.subscribers.price.toFixed(2);
  const commissionAmount = order.statistics.commissionAmount || 0;

  const totalSubscribers = order.subscribers.count || 1;
  const buyersCount = order.statistics.transactions.purchases || 0;
  const buyersPercent = ((buyersCount / totalSubscribers) * 100).toFixed(0);
  const chatsCount = order.subscribers.withMessages || 0;
  const chatsPercent = ((chatsCount / totalSubscribers) * 100).toFixed(0);

  const lastUpdated = formatTimeAgo(order.createdAt, t);

  return (
    <div className="flex items-start flex-col gap-1">
      {/* <Chip
        className="rounded-lg bg-gray-100 text-gray-900"
        classNames={{
          base: 'h-6 px-2 py-1 gap-1',
        }}
        size="sm"
        startContent={
          <div className={`w-2 h-2 rounded-full ${getStatusColor(order.status)}`}></div>
        }
      >
        <Typography variant="body3" className="text-gray-900">
          {getStatusLabel(order.status, t)}
        </Typography>
      </Chip> */}
      <Progress
        classNames={{
          indicator: '!bg-partner',
        }}
        color="primary"
        aria-label="Loading..."
        className="max-w-md"
        value={order.statistics.subscribedCount || 0}
        maxValue={order?.subscribers?.count}
      />
      <Typography variant="body3">
        {t('partner.orders.table.columns.subscribers')}: {order.statistics.subscribedCount}
        <span className="text-auth-muted"> / {order?.subscribers?.count}</span>
      </Typography>
      <div className="flex items-center gap-2 cursor-pointer">
        <ResponsiveTooltip
          content={interpolate(t('partner.orders.tooltips.campaignCost'), {
            totalCost: grossTotal.toFixed(2),
            pricePerFan,
            commission: commissionAmount.toFixed(2),
          })}
          placement="top"
        >
          <div className="flex items-center gap-1">
            <WalletIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              ${order.subscribers.price || 0}
            </Typography>
          </div>
        </ResponsiveTooltip>

        <ResponsiveTooltip content={t('partner.orders.tooltips.messages')} placement="top">
          <div className="flex items-center gap-1">
            <MessageIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              {order.subscribers.withMessages || 0}
            </Typography>
          </div>
        </ResponsiveTooltip>

        <ResponsiveTooltip content={t('partner.orders.tooltips.transactions')} placement="top">
          <div className="flex items-center gap-1">
            <TicketBillIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              ${netRevenueTotal.toFixed(2)}
            </Typography>
          </div>
        </ResponsiveTooltip>

        <ResponsiveTooltip content={t('partner.orders.tooltips.purchases')} placement="top">
          <div className="flex items-center gap-1">
            <MoneyIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              {order.statistics.transactions.purchases || 0}
            </Typography>
          </div>
        </ResponsiveTooltip>

        <ResponsiveTooltip
          content={interpolate(t('partner.orders.tooltips.buyersAndChats'), {
            buyersCount,
            buyersPercent,
            chatsCount,
            chatsPercent,
          })}
          placement="top"
        >
          <div className="flex items-center gap-1">
            <UserCheckIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              {order.statistics.subscribedCount || 0}
            </Typography>
          </div>
        </ResponsiveTooltip>

        <ResponsiveTooltip content={t('partner.orders.tooltips.subscribers')} placement="top">
          <div className="flex items-center gap-1">
            <GroupUserIc className="text-gray-600" />
            <Typography variant="body3" className="text-gray-900">
              {order.subscribers.count || 0}
            </Typography>
          </div>
        </ResponsiveTooltip>
      </div>
      <ResponsiveTooltip content={t('partner.orders.tooltips.arpu')} placement="top">
        <div className="flex items-center gap-2">
          <Typography variant="body3" className="text-auth-muted">
            {t('orders.campaign.arpu')}:
          </Typography>
          <Typography variant="body2" className="md:font-semibold">
            ${order.statistics.arpu.toFixed(2)}
          </Typography>
        </div>
      </ResponsiveTooltip>

      <ResponsiveTooltip content={t('partner.orders.tooltips.romi')} placement="top">
        <div className="flex items-center gap-2">
          <Typography variant="body3" className="text-auth-muted">
            {t('orders.campaign.romi')}:
          </Typography>
          <Typography
            variant="body2"
            className="md:font-semibold"
            style={{ color: order.statistics.romi < 0 ? '#F31260' : undefined }}
          >
            {order.statistics.romi.toFixed(2)}%
          </Typography>
        </div>
      </ResponsiveTooltip>

      <ResponsiveTooltip
        content={interpolate(t('partner.orders.tooltips.lastUpdated'), { date: lastUpdated })}
        placement="top"
      >
        <div className="flex items-center gap-1">
          <RefreshGreyIc />
          <span className="text-xs leading-4 text-auth-muted">
            {formatTimeAgo(order.createdAt, t)}
          </span>
        </div>
      </ResponsiveTooltip>
    </div>
  );
};

export default OfferCell;
